import { CCard, CContainer, CRow } from "@coreui/react-pro";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../../core/interfaces";
import { useActionBarStore, useIdentityStore, usePaginationStore } from "../../../core/store";
import { AppliedFilters, CellDraftBoolean, Divider, RoundedSquareIcon, SearchTable } from "../../components";
import { GridLoader } from "react-spinners";
import NoItems from "../../components/ui/no-items/NoItems";
import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../core/constants/route_name";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridSortModel, GridState } from "@mui/x-data-grid-pro";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { MyColumnMenu } from "../../components/table/mui/MyColumnMenu";
import { LOCAL_STORAGE_KEYS } from "../../../core/constants/local_storage";
import { useGridLocalStorage } from "../../components/table/hook/useGridLocalStorage";

// Ajusta la ruta si tu modelo es Banner
import { Banner, Blog } from "../../../core/models/db";
// Ajusta la ruta de tu custom hook:

import moment from "moment";
import { useTranslation } from "react-i18next";
import { useBanner } from "../../../core/hooks/db/fisioteduca/useFisioteduca";
import CellDate from "../../components/table/cell-date/CellDate";

const BannerPage = () => {
  const { identity } = useIdentityStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    gridState,
    saveGridState,
  } = useGridLocalStorage(LOCAL_STORAGE_KEYS.TABLE_BANNERS);

  const {
    isShowActionBar,
    reset,
    loadActionBar,
    showActionBar,
    hideActionBar,
  } = useActionBarStore((state) => state);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const INIT_PAGINATION = useMemo<Pagination>(
    () => ({
      page: 0,
      itemsPerPage: 10,
      filters: {},
      orderBy: null,
    }),
    []
  );

  const { pagination, setPagination, resetFilterRaws } = usePaginationStore();
  const [init, setInit] = useState(false);
  const [body, setBody] = useState<Banner[]>([]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  // Hook custom:
  const { getBanners } = useBanner();

  // 1) Setup paginación inicial + reset de filtros
  useEffect(() => {
    setPagination({ ...INIT_PAGINATION });
    resetFilterRaws();
    setInit(true);
  }, []);

  // 2) Mostrar ActionBar cuando haya selección
  useEffect(() => {
    if (selectedRowIds.length > 0) {
      const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER", "ROLE_MANAGER"];
      loadActionBar({
        delete: roleAllowed.includes(identity.role),
        table: "banner",
        count: selectedRowIds.length,
        items: selectedRowIds,
      });
      setLoading(false);
      showActionBar();
    } else {
      hideActionBar();
    }
  }, [selectedRowIds]);

  // 3) Cargar la lista cuando cambien paginación/filtros
  useEffect(() => {
    if (!init) return;
    loadData(pagination);
  }, [
    init,
    pagination?.page,
    pagination?.itemsPerPage,
    JSON.stringify(pagination?.orderBy),
    JSON.stringify(pagination?.filters),
  ]);

  // 4) Si se pide reset
  useEffect(() => {
    if (reset) {
      loadData({ ...INIT_PAGINATION });
      setSelectedRowIds([]);
    }
  }, [reset]);

  // 5) Función para cargar data
  async function loadData(pag = pagination) {
    setLoading(true);
    try {
      let pagAux = { ...pag, page: pag.page + 1 };
      if (!pagAux?.orderBy) {
        pagAux.orderBy = { field: "startPublicationDate", order: "desc" };
      }
      const response = await getBanners(pagAux);
      if (response?.ok) {
        setBody(response.item.rows);
        setPagination({
          ...pag,
          totalItems: response.item.pagination.totalItems,
          totalPages: response.item.pagination.totalPages,
        });
      } else {
        console.warn("Problema al devolver banners");
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  // 6) Columnas DataGrid
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: "title",
        headerName: "Título",
        flex: 1,
        sortable: true,
      },
      // {
      //   field: "isValid",
      //   headerName: "Estado",
      //   type: "boolean",
      //   align: "center",
      //   headerAlign: "center",
      //   width: 120,
      //   sortable: true,
      //   renderCell: (params: GridRenderCellParams<Banner, boolean>) => {
      //     return <CellDraftBoolean isValid={params.value} />;
      //   },
      // },
      {
        field: "startPublicationDate",
        headerName: "Empieza",
        type: "string",
        align: "center",
        headerAlign: "center",
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams<Blog, string>) => {
          return <CellDate startDate={params.value} hasTime={true} />;
        },
      },
      {
        field: "endPublicationDate",
        headerName: "Termina",
        type: "string",
        align: "center",
        headerAlign: "center",
        width: 180,
        sortable: true,
        renderCell: (params: GridRenderCellParams<Banner, string>) => {
          return <CellDate startDate={params.value} hasTime={true} />;
        },
      },
    ];
  }, [gridState.columnWidths]);

  // 7) rows con ID
  const rows = useMemo(() => body.map((item) => ({ ...item, id: item.id })), [body]);

  // 8) Paginación DataGridPro
  const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
    if (model.pageSize !== pagination?.itemsPerPage) {
      setPagination({ ...pagination, page: 0, itemsPerPage: model.pageSize });
    } else if (model.page !== pagination?.page) {
      setPagination({ ...pagination, page: model.page });
    }
  };

  // 9) Orden
  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setPagination({
        ...pagination,
        orderBy: { field, order: sort },
      });
    } else {
      setPagination({
        ...pagination,
        orderBy: null,
      });
    }
  };

  // 10) Click en fila => goToUpdate
  const goToUpdate = (item: Banner) => {
    const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER", "ROLE_MANAGER"];
    if (identity && roleAllowed.includes(identity.role)) {
      const pagesRoutes = t("routes", { returnObjects: true });
      const newRoute = ROUTE_NAME_ID_2({ id: item.id }, pagesRoutes, RouteName.bannerDetail);
      navigate(`${BASE_ROUTE}${newRoute}`, { state: { id: item.id } });
    }
  };

  // Agregar nuevo
  const goToAdd = () => {
    const pagesRoutes = t("routes", { returnObjects: true });
    const newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.bannerAdd);
    navigate(`${BASE_ROUTE}${newRoute}`);
  };

  const initialState = useMemo(
    () => ({
      pinnedColumns: gridState.pinnedColumns,
      columns: {
        orderedFields: gridState.columnOrder,
        columnWidths: gridState.columnWidths,
      },
    }),
    [gridState.pinnedColumns, gridState.columnOrder, gridState.columnWidths]
  );

  return (
    <CContainer>
      <div className="flex">
        <div className="w-full flex justify-between items-center pt-4 pb-5">
          <div>
            <span className="text-2xl sm:text-4xl" style={{ fontWeight: 500 }}>
              Banners
            </span>
          </div>
          <div
            onClick={goToAdd}
            className="px-3 py-1 rounded-xl flex items-center bg-principalColor text-white hover:bg-principalColor-500 hover:cursor-pointer"
          >
            <span className="text-2xl mr-2">+</span> <span>Agregar</span>
          </div>
        </div>
      </div>

      <CCard className="rounded-xl p-3">
        {/* Filtros y Búsqueda */}
        {/* <CRow>
          <SearchTable nameColumn={["title"]} label={["Título"]} />
        </CRow>
        <Divider marginX={-16} />
        <CRow>
          <AppliedFilters />
        </CRow>
        <Divider marginX={-16} /> */}
        <CRow>
          {loading && (
            <div className="w-full h-[250px] flex justify-center items-center">
              <GridLoader color="#3d4f65" size={15} speedMultiplier={0.7} />
            </div>
          )}

          {body && body.length > 0 && pagination && pagination?.totalPages && (
            <div style={{ visibility: loading ? "hidden" : "visible" }}>
              <DataGridPro
                localeText={{}}
                slots={{
                  columnMenu: MyColumnMenu,
                }}
                initialState={initialState}
                onStateChange={(newState: GridState) => {
                  const nextColumnOrder = newState.columns.orderedFields;
                  const nextPinned = newState.pinnedColumns;
                  saveGridState({
                    columnOrder: nextColumnOrder,
                    pinnedColumns: nextPinned,
                  });
                }}
                rows={rows}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick={false}
                onRowClick={(params: GridRowParams, event) => {
                  if (selectedRowIds.length === 0) {
                    event.stopPropagation();
                    goToUpdate(params.row as Banner);
                  }
                }}
                rowSelectionModel={selectedRowIds}
                onRowSelectionModelChange={(newSelection) => {
                  setSelectedRowIds(newSelection as string[]);
                }}
                pagination
                paginationMode="server"
                rowCount={pagination.totalItems}
                pageSizeOptions={[5, 10, 25, 50, 100]}
                paginationModel={{
                  page: pagination?.page,
                  pageSize: pagination?.itemsPerPage,
                }}
                onPaginationModelChange={handlePaginationModelChange}
                sortingMode="server"
                onSortModelChange={handleSortModelChange}
                slotProps={{
                  baseCheckbox: {
                    icon: <RoundedSquareIcon checked={false} />,
                    checkedIcon: <RoundedSquareIcon checked={true} />,
                    indeterminateIcon: <RoundedSquareIcon indeterminate />,
                    style: { padding: 0 },
                  },
                }}
                sx={{
                  border: 0,
                  "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-row": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-columnSeparator": {
                    visibility: "hidden",
                  },
                  "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": {
                    visibility: "visible",
                  },
                  "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                    display: "none",
                  },
                }}
              />
            </div>
          )}

          {!loading && rows.length === 0 && <NoItems />}
        </CRow>
      </CCard>
    </CContainer>
  );
};

export default BannerPage;
